/* @flow */

import classNames from 'classnames';
import { Formik, Form, Field } from 'formik';
import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import * as Yup from 'yup';

import styles from 'src/shared/app/authentication/component/base/AuthenticationBase.style';

import AuthenticationFormHeader from 'src/shared/app/authentication/component/base/AuthenticationFormHeader';
import authMessages from 'src/shared/app/authentication/l10n/authenticationL10n';
import messages from 'src/shared/app/authentication/l10n/retrieveUsernameL10n';
import {
  firstNameFieldMaxLength,
  lastNameFieldMaxLength,
  extraFieldNameFieldMaxLength,
} from 'src/shared/app/authentication/settings/authenticationSettings';
import FormikFormGroup from 'src/shared/app/base/component/data-entry/form/FormikFormGroup';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import { contactRoute } from 'src/shared/app/base/route/setting/routeSettings';
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';
import userMessages from 'src/shared/domain/user/l10n/userL10n';
import SecondaryButton from 'src/shared/ui/component/button/SecondaryButton';
import DiscreteLink from 'src/shared/ui/component/link/DiscreteLink';
import NoteText from 'src/shared/ui/component/text/NoteText';

type Props = {
  extraFieldLegend: ?string,
  extraFieldLabel: ?string,
  extraFieldPlaceholder: ?string,
  handleSubmit: (Object) => Promise<any>,
};

const useStyles = createUseStyles(styles);

const RetrievenUsernameFormDumb = ({
  extraFieldLegend,
  extraFieldLabel,
  extraFieldPlaceholder,
  handleSubmit,
}: Props): React.Node => {
  const schema = Yup.object().shape({
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    extraData: Yup.string().required(),
  });

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();
  const { braindatePlatform } = useEventBraindateName();
  const [contactPath] = useRoutes(contactRoute);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <Formik
      initialValues={{ firstName: '', lastName: '', extraData: '' }}
      validationSchema={schema}
      validateOnBlur={true}
      onSubmit={(values, { setSubmitting, setStatus }) =>
        handleSubmit(values).catch(({ errors }) => {
          setSubmitting(false);

          const [error] = Object.values(errors || {});

          if (error) {
            setStatus(error);
          }
        })
      }
    >
      {({ status, dirty, isSubmitting }) => {
        const helperElt = status && (
          <div className={classes.support}>
            <DiscreteLink href={contactPath}>
              {intl.formatMessage(authMessages.contact)}
            </DiscreteLink>
          </div>
        );

        const extraFieldNoteElt = extraFieldLegend && !status && (
          <NoteText>{extraFieldLegend}</NoteText>
        );

        return (
          <Form
            className={classNames(classes.form, classes['form-isLong'])}
            noValidate
          >
            <AuthenticationFormHeader withError={!!status}>
              {status ||
                intl.formatMessage(messages.legend, {
                  platform: braindatePlatform,
                })}
            </AuthenticationFormHeader>
            <Field
              id="firstName"
              name="firstName"
              type="text"
              component={FormikFormGroup}
              componentOpts={{
                label: intl.formatMessage(userMessages.firstName),
              }}
              maxLength={firstNameFieldMaxLength}
              required
              autoFocus
            />
            <Field
              id="lastName"
              name="lastName"
              type="text"
              component={FormikFormGroup}
              componentOpts={{
                label: intl.formatMessage(userMessages.lastName),
              }}
              maxLength={lastNameFieldMaxLength}
              required
            />
            <Field
              id="extraData"
              name="extraData"
              type="text"
              component={FormikFormGroup}
              componentOpts={{
                label: extraFieldLabel,
              }}
              placeholder={extraFieldPlaceholder}
              maxLength={extraFieldNameFieldMaxLength}
              required
            />
            {extraFieldNoteElt}
            <div className={classes.submit}>
              <SecondaryButton isDisabled={!dirty} isSubmitting={isSubmitting}>
                {intl.formatMessage(messages.submit)}
              </SecondaryButton>
            </div>
            {helperElt}
          </Form>
        );
      }}
    </Formik>
  );
};

export default compose(React.memo)(RetrievenUsernameFormDumb);
